// MobileBottomNavbar.jsx
import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faPlus,
  faLayerGroup,
  faEllipsisH,
  faLink,
  faSignOutAlt,
  faRocket,
  faSliders,
  faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";
import { faBell } from "@fortawesome/free-regular-svg-icons";
import { faShopify } from "@fortawesome/free-brands-svg-icons";

const MobileBottomNavbar = ({ user = {}, handleLogout, unreadCount = 0 }) => {
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const isActive = (path) => location.pathname === path;
  const isCreateActive =
    location.pathname === "/dashboard/create-ad" ||
    location.pathname === "/dashboard/create-ad-ai";

  const tabCls = (active) =>
    `flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition-all ${
      active ? "text-[#221C51]" : "text-gray-400"
    }`;

  const dot = <span className="w-1 h-1 rounded-full bg-[#221C51] mt-0.5" />;

  const drawerLinks = [
    { to: "/dashboard/ai-launcher",        icon: faWandMagicSparkles, label: "AI Launcher", badge: "NEW" },
    { to: "/dashboard/shopify-ads",        icon: faShopify,           label: "Shopify Ads" },
    { to: "/dashboard/ads-ruleset",        icon: faSliders,           label: "Rules" },
    { to: "/dashboard/connected-platforms",icon: faLink,              label: "Platforms" },
  ];

  return (
    <>
      {/* ── Slide-up More Drawer ── */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex flex-col justify-end">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
          />

          {/* Sheet */}
          <div className="relative z-50 bg-white rounded-t-3xl shadow-2xl pb-10 pt-2 px-5"
            style={{ animation: "slideUp 0.25s ease-out" }}>

            {/* Handle */}
            <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-5" />

            {/* User card */}
            <div className="flex items-center gap-3 p-3.5 bg-[#F7F7FC] rounded-2xl mb-4 border border-[#221C51]/10">
              <div className="w-10 h-10 rounded-full bg-[#221C51] flex items-center justify-center text-white font-bold text-base shrink-0">
                {user.name ? user.name[0].toUpperCase() : "?"}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[#221C51] text-sm truncate">{user.name || "User"}</p>
                <p className="text-xs text-gray-400 truncate">{user.email || ""}</p>
              </div>
              <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full shrink-0 ${
                user.isPremium ? "bg-[#221C51] text-white" : "bg-gray-100 text-gray-500"
              }`}>
                {user.isPremium ? "PRO" : "Basic"}
              </span>
            </div>

            {/* Nav links */}
            <div className="space-y-1 mb-3">
              {drawerLinks.map(({ to, icon, label, badge }) => (
                <Link
                  key={to}
                  to={to}
                  onClick={() => setDrawerOpen(false)}
                  className={`flex items-center gap-3.5 px-4 py-3.5 rounded-2xl transition-all font-medium text-sm ${
                    isActive(to)
                      ? "bg-[#221C51] text-white"
                      : "text-[#221C51] hover:bg-[#F0F0FA]"
                  }`}
                >
                  <FontAwesomeIcon icon={icon} className="text-base w-5" />
                  <span className="flex-1">{label}</span>
                  {badge && (
                    <span className="text-[9px] font-bold bg-gradient-to-r from-purple-500 to-pink-500 text-white px-1.5 py-0.5 rounded-full">
                      {badge}
                    </span>
                  )}
                </Link>
              ))}
            </div>

            {/* Upgrade banner — non-premium only */}
            {!user.isPremium && (
              <Link
                to="/dashboard/billing"
                onClick={() => setDrawerOpen(false)}
                className="flex items-center gap-3 px-4 py-3.5 rounded-2xl bg-gradient-to-r from-[#221C51] to-[#4A3D9A] text-white mb-3 shadow-md"
              >
                <FontAwesomeIcon icon={faRocket} className="text-base" />
                <span className="font-semibold text-sm">Upgrade to Pro</span>
              </Link>
            )}

            {/* Log out */}
            <button
              onClick={() => { setDrawerOpen(false); handleLogout?.(); }}
              className="flex items-center gap-3.5 px-4 py-3.5 rounded-2xl text-red-500 hover:bg-red-50 transition-all w-full font-medium text-sm"
            >
              <FontAwesomeIcon icon={faSignOutAlt} className="text-base w-5" />
              Log Out
            </button>
          </div>
        </div>
      )}

      {/* ── Bottom Tab Bar ── */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur-md border-t border-gray-100 shadow-xl">
        <div className="flex items-end justify-around px-2 pt-1 pb-3">

          {/* Overview */}
          <Link to="/dashboard/statistics" className={tabCls(isActive("/dashboard/statistics"))}>
            <FontAwesomeIcon icon={faChartLine} className="text-[18px]" />
            <span className="text-[10px] font-medium">Overview</span>
            {isActive("/dashboard/statistics") && dot}
          </Link>

          {/* Campaigns */}
          <Link to="/dashboard/campaigns" className={tabCls(isActive("/dashboard/campaigns"))}>
            <FontAwesomeIcon icon={faLayerGroup} className="text-[18px]" />
            <span className="text-[10px] font-medium">Campaigns</span>
            {isActive("/dashboard/campaigns") && dot}
          </Link>

          {/* Centre Create button */}
          <Link to="/dashboard/create-ad-ai" className="flex flex-col items-center -mt-6">
            <div className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-transform ${
              isCreateActive ? "scale-110" : "hover:scale-105"
            } bg-[#221C51]`}>
              <FontAwesomeIcon icon={faPlus} className="text-white text-xl" />
            </div>
            <span className={`text-[10px] font-medium mt-1 ${isCreateActive ? "text-[#221C51]" : "text-gray-400"}`}>
              Create
            </span>
          </Link>

          {/* Notifications */}
          <Link to="/dashboard/notifications" className={tabCls(isActive("/dashboard/notifications"))}>
            <div className="relative">
              <FontAwesomeIcon icon={faBell} className="text-[18px]" />
              {unreadCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[9px] font-bold leading-none">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </div>
            <span className="text-[10px] font-medium">Alerts</span>
            {isActive("/dashboard/notifications") && dot}
          </Link>

          {/* More */}
          <button
            onClick={() => setDrawerOpen(true)}
            className={tabCls(drawerOpen)}
          >
            <FontAwesomeIcon icon={faEllipsisH} className="text-[18px]" />
            <span className="text-[10px] font-medium">More</span>
            {drawerOpen && dot}
          </button>

        </div>
      </div>

      {/* Slide-up keyframe */}
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
      `}</style>
    </>
  );
};

export default MobileBottomNavbar;
